import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { type FindStoreResult, type ProductStoreStockViewModel, SetUserPreferredStore } from 'autogen/swagger/all';
import { ColorNordicGrey4, Spacing48, Spacing16 } from 'autogen/design-tokens/tokens';
import { Button } from 'components/Button';
import UAEventTracking, { type TrackingCategory } from 'modules/tracking/UAEventTracking';
import StoreItem from './storeItem';
import { OnSelectParcelPickupContext } from '../shared/helpers';

const Container = styled.div`
    padding-bottom: ${Spacing48};
`;

const ButtonContainer = styled.div`
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    padding: ${Spacing16};
    background-color: ${ColorNordicGrey4};
`;

const StyledButton = styled(Button)`
    width: 100%;
`;

const ParcelPickupList = styled.div`
    display: grid;
    gap: ${Spacing16};
    margin-bottom: ${Spacing16};
`;

interface IStoreList extends FindStoreResult {
    translations: {
        chooseStoreButtonText: string;
    };
    initialPreferredStore?: ProductStoreStockViewModel['initialPreferredStore'];
    trackingCategory?: TrackingCategory;
    showSelectParcelShop?: boolean;
}

export default function StoreList({
    parcelShops,
    translations,
    initialPreferredStore,
    trackingCategory,
    showSelectParcelShop
}: IStoreList): JSX.Element {
    const { chooseStoreButtonText } = translations;
    const [activeElement, setActiveElement] = useState<FindStoreResult['productId']>(initialPreferredStore);
    const onSelectParcelPickup = useContext(OnSelectParcelPickupContext);

    function onChooseParcelPickup() {
        if (activeElement) {
            SetUserPreferredStore({ storeId: activeElement }).then(() => {
                if (onSelectParcelPickup) onSelectParcelPickup();
            });
        }

        if (trackingCategory) {
            UAEventTracking({ eventCategory: trackingCategory, eventAction: 'selectStore' });
        }
    }

    return (
        <Container>
            <ParcelPickupList>
                {parcelShops.map((parcelShop) => {
                    const { id } = parcelShop;

                    return (
                        <StoreItem
                            key={id}
                            {...parcelShop}
                            selectCallback={() => setActiveElement(id)}
                            isActive={activeElement === id}
                            showSelectParcelShop={showSelectParcelShop}
                        />
                    );
                })}
            </ParcelPickupList>
            {showSelectParcelShop && !!parcelShops.length && (
                <ButtonContainer>
                    <StyledButton onClick={onChooseParcelPickup} disabled={!activeElement}>
                        {chooseStoreButtonText}
                    </StyledButton>
                </ButtonContainer>
            )}
        </Container>
    );
}
